import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader, Search, X } from 'lucide-react';
import { elasticsearchService } from '../../services/elasticsearchService';
import { useDebounce } from '../../hooks/useDebounce';
import { PlayerProfile } from '../PlayerProfile';
import { cn } from '../../utils/cn';

interface PlayerSearchHit {
  _id: string;
  _source: {
    uuid: string;
    lastKnownName: string;
    nickname: string;
    lastKnownIp: string;
  };
}

const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

export interface PlayerSelection {
  uuid: string;
  name: string | null;
}

export interface PlayerSearchFieldProps {
  /** Currently selected player (controlled). */
  value: PlayerSelection | null;
  onChange: (selection: PlayerSelection | null) => void;
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
  /** Compact single-row styling for inline forms. */
  compact?: boolean;
}

/**
 * Username-first player picker backed by the same `cdu-players-v1` search the
 * global player search uses. Pasting a full UUID is accepted directly.
 *
 * The results dropdown renders through a portal with fixed positioning so it can
 * never be clipped or covered by sibling rows/cards (stacking-context safe).
 */
export function PlayerSearchField({
  value,
  onChange,
  placeholder = 'Search player by name…',
  className,
  autoFocus = false,
  compact = false,
}: PlayerSearchFieldProps) {
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 300);
  const [results, setResults] = useState<PlayerSearchHit[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const listId = useId();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [rect, setRect] = useState<{ top: number; left: number; width: number } | null>(null);

  const isUuid = UUID_RE.test(query.trim());
  const showDropdown =
    open && (isUuid || results.length > 0 || (!searching && debouncedQuery.trim().length >= 2));

  useEffect(() => {
    if (debouncedQuery !== query || debouncedQuery.trim().length < 2 || UUID_RE.test(debouncedQuery.trim())) {
      setResults([]);
      setSearching(false);
      setSearchError('');
      return;
    }
    let cancelled = false;
    setSearching(true);
    setSearchError('');
    elasticsearchService
      .searchPlayers(debouncedQuery.trim())
      .then((res) => {
        if (!cancelled) {
          setResults((res?.hits?.hits as PlayerSearchHit[]) ?? []);
          setActiveIndex(0);
          setOpen(true);
        }
      })
      .catch(() => {
        if (!cancelled) { setResults([]); setSearchError('Player search is unavailable. Try again or paste a UUID.'); }
      })
      .finally(() => {
        if (!cancelled) setSearching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, query]);

  // Keep the portal dropdown anchored to the input (scroll/resize aware).
  useLayoutEffect(() => {
    if (!showDropdown) return;
    const update = () => {
      const el = containerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setRect({ top: r.bottom + 4, left: r.left, width: r.width });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [showDropdown]);

  // Close on outside click (input container AND portal content count as inside).
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const select = (uuid: string, name: string | null) => {
    onChange({ uuid, name });
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  if (value) {
    return (
      <div
        className={cn(
          'flex items-center justify-between gap-2 rounded-lg bg-accent-500/10 border border-accent-500/20',
          compact ? 'px-2 py-1' : 'p-2',
          className,
        )}
      >
        <PlayerProfile uuid={value.uuid} name={value.name ?? undefined} showUuid={false} inline />
        <button
          type="button"
          onClick={() => onChange(null)}
          className="text-glass-400 hover:text-white flex-shrink-0 p-1"
          aria-label="Clear player"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  const dropdown =
    showDropdown && rect
      ? createPortal(
          <div
            ref={dropdownRef}
            id={listId}
            role="listbox"
            aria-label="Matching players"
            data-outside-click-ignore
            style={{ position: 'fixed', top: rect.top, left: rect.left, width: rect.width, zIndex: 10000 }}
            className="glass-card border border-glass-200/20 rounded-lg overflow-hidden shadow-xl shadow-black/50 max-h-56 overflow-y-auto"
          >
            {isUuid ? (
              <button
                id={`${listId}-0`}
                role="option"
                aria-selected={activeIndex === 0}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  select(query.trim().toLowerCase(), null);
                }}
                className="w-full text-left px-3 py-2 hover:bg-glass-200/10 text-sm text-white"
              >
                Use UUID <span className="font-mono text-xs text-glass-400">{query.trim()}</span>
              </button>
            ) : results.length > 0 ? (
              results.map((hit, index) => (
                <button
                  key={hit._id}
                  id={`${listId}-${index}`}
                  role="option"
                  aria-selected={activeIndex === index}
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    select(hit._source.uuid, hit._source.lastKnownName || null);
                  }}
                  className={cn('w-full text-left px-3 py-2 hover:bg-glass-200/10', activeIndex === index && 'bg-glass-200/10')}
                >
                  <span className="text-sm text-white">{hit._source.lastKnownName || hit._source.uuid}</span>
                </button>
              ))
            ) : (
              !searching && (
                <div className="px-3 py-2 text-xs text-glass-500">
                  {searchError || 'No players found. Paste a UUID to use it directly.'}
                </div>
              )
            )}
          </div>,
          document.body,
        )
      : null;

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <Search
        className={cn(
          'absolute left-3 top-1/2 -translate-y-1/2 text-glass-400',
          compact ? 'w-3.5 h-3.5' : 'w-4 h-4',
        )}
      />
      <input
        type="text"
        role="combobox"
        aria-label={placeholder}
        aria-autocomplete="list"
        aria-expanded={showDropdown}
        aria-controls={showDropdown ? listId : undefined}
        aria-activedescendant={showDropdown && (isUuid || results.length) ? `${listId}-${activeIndex}` : undefined}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setResults([]);
          setActiveIndex(0);
          setOpen(true);
        }}
        onKeyDown={e => {
          if (e.key === 'Escape') { setOpen(false); return; }
          const count = isUuid ? 1 : results.length;
          if (!count) return;
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            setOpen(true);
            const next = (activeIndex + (e.key === 'ArrowDown' ? 1 : -1) + count) % count;
            setActiveIndex(next);
            document.getElementById(`${listId}-${next}`)?.scrollIntoView({ block: 'nearest' });
          } else if (e.key === 'Enter' && showDropdown) {
            e.preventDefault();
            if (isUuid) select(query.trim().toLowerCase(), null);
            else { const hit = results[activeIndex]; if (hit) select(hit._source.uuid, hit._source.lastKnownName || null); }
          }
        }}
        onFocus={() => setOpen(true)}
        className={cn('glass-input w-full text-sm', compact ? 'pl-9 py-1.5' : 'pl-10')}
        placeholder={placeholder}
        autoFocus={autoFocus}
      />
      {searching && (
        <Loader
          className={cn(
            'absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-glass-400',
            compact ? 'w-3.5 h-3.5' : 'w-4 h-4',
          )}
        />
      )}
      {dropdown}
    </div>
  );
}
